import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { tagHotjarRecording } from '../../helpers/util'
import useProfile from '../../hooks/useProfile'
import useAuth from '../../hooks/useAuth'
import AppRoutes from '../../routes/appRoutes'
import ContentLayout from '../../components/ContentLayout/ContentLayout'
import MainHeading from '../../components/MainHeading/MainHeading'
import CardInfo from '../../components/CardInfo/CardInfo'
import Button from '../../components/Button/Button'
import InputText from '../../components/InputText/InputText'
import InputPassword from '../../components/InputText/components/InputPassword'
import { useFormik } from 'formik'
import {
    initialValues,
    validationSchema,
} from '../../forms/ProfilePasswordForm'
import useGlobalLoading from '../../hooks/useGlobalLoading'
import useAlert from '../../hooks/useAlert'
import { useHistory } from 'react-router-dom'

const ProfilePasswordPage = () => {
    const [error, setError] = useState('')
    const { handleChangePassword } = useProfile()
    const { user, impersonatedBy } = useAuth()
    const { changeLoading, isLoading } = useGlobalLoading()
    const { handleChangeAlert, handleChangeOpen } = useAlert()
    const history = useHistory()

    const onPasswordChangedSuccess = response => {
        changeLoading(false)
        if (response?.status == 200) {
            resetForm()
            handleChangeAlert({
                title: 'Password Change Successful',
                message: 'Your password is updated successful',
                textCancel: 'Return to Profile',
                onCancel: () => {
                    history?.push(AppRoutes.profile())
                    handleChangeOpen(false)
                },
                type: 'success',
            })
        }
    }

    const onPasswordChangedError = err => {
        changeLoading(false)
        tagHotjarRecording('API Error')
        let errs = err.response.data.errors
        let message = err.response.data.message

        if (errs) {
            let allErrors = Object.keys(errs)
                .map(function(elem, i) {
                    var key = Object.keys(errs)[i]
                    return errs[key][0] + ' \n'
                })
                .join(' ')

            let listErr = allErrors
                .split('\n')
                .filter(i => i != null && i != '')
                .map((item, key) => {
                    return (
                        <span key={key}>
                            {item}
                            <br />
                        </span>
                    )
                })
            setError(listErr)
        } else {
            setError(message)
        }
    }

    const processSubmit = values => {
        setError('')
        changeLoading(true)
        handleChangePassword(
            {
                ...values,
                userId: user?.id,
            },
            onPasswordChangedSuccess,
            onPasswordChangedError,
        )
    }

    const {
        handleSubmit,
        getFieldProps,
        errors,
        touched,
        isValid,
        values,
        resetForm,
    } = useFormik({
        initialValues: initialValues,
        validationSchema: validationSchema,
        onSubmit: formValues => {
            processSubmit(formValues)
        },
    })

    const hasDifferences =
        JSON.stringify(initialValues) != JSON.stringify(values)

    const renderContent = () => {
        return (
            <div
                onSubmit={handleSubmit}
                className="w-full h-auto flex flex-col items-start justify-start transition-all"
            >
                <p className="text-black70 font-medium text-base max-w-full truncate md:block flex flex-col items-start justify-start">
                    Set new password to the account registered for{' '}
                    <b className="text-black90 max-w-full truncate">
                        {user?.email}{' '}
                    </b>
                </p>
                <p className="text-sm font-normal text-textDarkGray">
                    Must contain at least 6 characters, one letter, and one
                    number.
                </p>

                <div className="flex w-full flex-col items-start justify-start gap-y-1 mt-6 transition-all">
                    <InputText
                        autoComplete="current-password"
                        {...getFieldProps('old_password')}
                        error={
                            touched['old_password'] && errors['old_password']
                        }
                        type="password"
                        additionalClass="w-full"
                        label="Old Password"
                        isRequired
                    />
                    <InputPassword
                        autoComplete="new-password"
                        {...getFieldProps('new_password')}
                        error={
                            touched['new_password'] && errors['new_password']
                        }
                        label="New Password"
                        isRequired
                    />

                    <InputPassword
                        autoComplete="new-password"
                        {...getFieldProps('new_password_confirmation')}
                        error={
                            touched['new_password_confirmation'] &&
                            errors['new_password_confirmation']
                        }
                        label="Confirm new Password"
                        isRequired
                    />

                    {error && (
                        <p className="w-full text-left h-auto text-error font-normal text-base my-4 flex flex-col items-start justify-start">
                            {error}
                        </p>
                    )}
                </div>
            </div>
        )
    }

    return (
        <ContentLayout>
            <MainHeading title="Change Password" />
            <form
                onSubmit={handleSubmit}
                className="h-auto lg:w-220 w-full md:p-0 p-4 flex md:mt-12 flex-col items-center justify-start"
            >
                <CardInfo
                    title="Set a new Password"
                    subTitle="Change your password"
                    titleClass="md:w-auto w-full"
                    headerActions={[
                        <Button
                            key="submitButton"
                            variant="secondary"
                            className="md:w-auto w-full"
                            text="Save password"
                            type="submit"
                            disabled={
                                !isValid ||
                                isLoading ||
                                !hasDifferences ||
                                impersonatedBy === 'salesperson'
                            }
                        />,
                    ]}
                    content={renderContent()}
                />
            </form>
        </ContentLayout>
    )
}

ProfilePasswordPage.propTypes = {
    impersonatedBy: PropTypes.string,
    user: PropTypes.shape({
        id: PropTypes.number,
        email: PropTypes.string,
    }),
}

export default ProfilePasswordPage
